const READABLE_LABEL_COLLATOR = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });

type QuantityFamily = 'volume' | 'mass' | 'count';

type ParsedQuantity = {
  family: QuantityFamily;
  magnitude: number;
};

const VOLUME_UNIT_TO_ML: Record<string, number> = {
  ml: 1,
  millilitre: 1,
  milliliter: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  litre: 1000,
  liter: 1000,
};

const MASS_UNIT_TO_MG: Record<string, number> = {
  mg: 1,
  g: 1000,
  gr: 1000,
  kg: 1_000_000,
};

function normalizeUnitToken(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, '')
    .replace(/[.·]/g, '');
}

function leadingNumber(label: string): number | null {
  const match = label.trim().match(/^(\d+(?:[.,]\d+)?)/u);
  if (!match) return null;
  const n = Number.parseFloat(match[1].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function parseSortableQuantity(label: string): ParsedQuantity | null {
  const trimmed = label.trim();
  const match = trimmed.match(/^(\d+(?:[.,]\d+)?)\s*([^\d\s].*)?$/u);
  if (!match) return null;

  const magnitude = Number.parseFloat(match[1].replace(',', '.'));
  if (!Number.isFinite(magnitude)) return null;

  const unitRaw = (match[2] ?? '').trim();
  if (!unitRaw) return { family: 'count', magnitude };

  const unit = normalizeUnitToken(unitRaw);
  const mlFactor = VOLUME_UNIT_TO_ML[unit];
  if (mlFactor != null) return { family: 'volume', magnitude: magnitude * mlFactor };

  const mgFactor = MASS_UNIT_TO_MG[unit];
  if (mgFactor != null) return { family: 'mass', magnitude: magnitude * mgFactor };

  return null;
}

/** Locale-aware sort; quantities with units sort by real magnitude (e.g. 1000 ml before 1 l). */
export function compareReadableLabels(a: string, b: string): number {
  const pa = parseSortableQuantity(a);
  const pb = parseSortableQuantity(b);
  if (pa && pb && pa.family === pb.family) {
    const diff = pa.magnitude - pb.magnitude;
    if (diff !== 0) return diff;
    if (pa.family !== 'count') {
      const na = leadingNumber(a);
      const nb = leadingNumber(b);
      if (na != null && nb != null && na !== nb) return nb - na;
    }
  }
  return READABLE_LABEL_COLLATOR.compare(a, b);
}
